// Calendars & tasks: the GraphQL surface (docs/integrations-contract.md, FROZEN).
//
// Every operation here is live on the server (P0–P3, P5; contract §9). Apple
// (P4) never reaches the server: see services/appleEventKit.ts.

import { GraphqlResponseError, graphqlRequest, isGraphqlConfigured } from './graphqlClient';
import { TODO_FIELDS, type BackendTodo } from './todosApi';
import { TODO_LIST_FIELDS } from './todoListsApi';
import type {
  TodoList,
  IntegrationAccount,
  IntegrationCapability,
  IntegrationClient,
  IntegrationGrant,
  IntegrationProvider,
  CalendarEvent,
  CalendarFetchIssue,
  PrayerKey,
} from '../types';

export { isGraphqlConfigured as isIntegrationsApiConfigured };

/** Contract §1.1 `extensions.code` values the UI branches on. */
export const INTEGRATION_ERROR = {
  unavailable: 'INTEGRATIONS_UNAVAILABLE',
  notEnabled: 'INTEGRATIONS_NOT_ENABLED',
  proRequired: 'PRO_REQUIRED',
  notFound: 'NOT_FOUND',
  needsReauth: 'NEEDS_REAUTH',
  /** C12: the provider was down / timed out / rate-limited during a live call. */
  providerUnavailable: 'PROVIDER_UNAVAILABLE',
  /** C15/C16/C19: a missing grant; `extensions.grant` says which (scopeRequiredGrant). */
  scopeRequired: 'SCOPE_REQUIRED',
  /** C20: more than 6 changed prayer-time writes in 24 h. */
  rateLimited: 'RATE_LIMITED',
  /** C22: createTodoList at a provider whose account is still `paused` for a Pro user — syncIntegrations(accountId) resumes it. */
  accountPaused: 'ACCOUNT_PAUSED',
  /** C22: the provider refused the new list; `extensions.reason` is `limit` or `rejected`. Never auto-retried. */
  providerRejected: 'PROVIDER_REJECTED',
} as const;

// Every field the UI reads, and nothing it doesn't: tokens and cursors never
// leave the server, and `lastError` is server-only (C6).
const ACCOUNT_FIELDS = `
  id provider email status lastErrorCode grants
  settings { selectedCalendarIds writePrayerTimes rhemnCalendarId prayerTimesWrittenThrough meetingPrompts }
  connectedAt lastSyncedAt syncAvailableAt
`;

/**
 * The rollout flag (contract C9). Auth only. Any GraphQL-level "no" (an older
 * server without the field, INTEGRATIONS_UNAVAILABLE) reads as false, the safe
 * default. Network failures and timeouts still throw, so the caller can keep
 * its cached value instead of hiding the entry on a flaky connection.
 */
export const getIntegrationsEnabled = async (): Promise<boolean> => {
  try {
    const data = await graphqlRequest<{ integrationsEnabled: boolean }>(
      'query IntegrationsEnabled { integrationsEnabled }',
    );
    return data.integrationsEnabled === true;
  } catch (err) {
    if (err instanceof GraphqlResponseError) return false;
    throw err;
  }
};

/** [P0] Auth only (C3): a lapsed-Pro user still sees `paused` accounts. */
export const getIntegrationAccounts = async (): Promise<IntegrationAccount[]> => {
  const data = await graphqlRequest<{ integrationAccounts: IntegrationAccount[] }>(
    `query IntegrationAccounts { integrationAccounts { ${ACCOUNT_FIELDS} } }`,
  );
  return data.integrationAccounts;
};

export interface BeginIntegrationConnectInput {
  provider: IntegrationProvider;
  client: IntegrationClient;
  capability?: IntegrationCapability;
  /** Chrome only: chrome.identity.getRedirectURL('integrations'). Ignored elsewhere. */
  returnTo?: string;
}

export interface BeginIntegrationConnectPayload {
  authorizeUrl: string;
  /** The exact URL/scheme the flow finishes on (§3). */
  returnTo: string;
  /** ISO; the signed state dies at this instant (10 min). */
  expiresAt: string;
}

/** [P0] Auth + cohort + Pro. */
export const beginIntegrationConnect = async (
  input: BeginIntegrationConnectInput,
): Promise<BeginIntegrationConnectPayload> => {
  const data = await graphqlRequest<{ beginIntegrationConnect: BeginIntegrationConnectPayload }>(
    `mutation BeginIntegrationConnect($input: BeginIntegrationConnectInput!) {
      beginIntegrationConnect(input: $input) { authorizeUrl returnTo expiresAt }
    }`,
    { input },
  );
  return data.beginIntegrationConnect;
};

export interface DisconnectIntegrationPayload {
  ok: boolean;
  provider: IntegrationProvider;
  removedTodoListIds: string[];
  keptTodoListIds: string[];
}

/** [P0] Auth only (C3): revoking access never sits behind the paywall. */
export const disconnectIntegration = async (
  id: string,
  keepLists: boolean,
): Promise<DisconnectIntegrationPayload> => {
  const data = await graphqlRequest<{ disconnectIntegration: DisconnectIntegrationPayload }>(
    `mutation DisconnectIntegration($id: ID!, $keepLists: Boolean!) {
      disconnectIntegration(id: $id, keepLists: $keepLists) { ok provider removedTodoListIds keptTodoListIds }
    }`,
    { id, keepLists },
  );
  return data.disconnectIntegration;
};

// ── [P1]/[P2]: synced task lists (contract §7a, §7c) ────────────────────────

export interface IntegrationTaskList {
  externalId: string;
  name: string;
  linked: boolean;
  /** Google "My Tasks" / Todoist Inbox: pre-selected on first connect (F3). */
  isDefault: boolean;
  todoListId: string | null;
}

/** Auth + Pro. PROVIDER_UNAVAILABLE (C12) / SCOPE_REQUIRED grant:"tasks" (C15) are typed. */
export const getIntegrationTaskLists = async (accountId: string): Promise<IntegrationTaskList[]> => {
  const data = await graphqlRequest<{ integrationTaskLists: IntegrationTaskList[] }>(
    `query IntegrationTaskLists($accountId: ID!) {
      integrationTaskLists(accountId: $accountId) { externalId name linked isDefault todoListId }
    }`,
    { accountId },
  );
  return data.integrationTaskLists;
};

export interface SetLinkedTaskListsPayload {
  taskLists: IntegrationTaskList[];
  /** Every linked TodoList for this account after the change. */
  todoLists: TodoList[];
  /** Unlinked → the Rhemn copies were deleted (Q3: Undo = re-link). */
  removedTodoListIds: string[];
}

/** Sets the EXACT linked set for the account. */
export const setLinkedTaskLists = async (
  accountId: string,
  externalIds: string[],
): Promise<SetLinkedTaskListsPayload> => {
  const data = await graphqlRequest<{ setLinkedTaskLists: SetLinkedTaskListsPayload }>(
    `mutation SetLinkedTaskLists($accountId: ID!, $externalIds: [ID!]!) {
      setLinkedTaskLists(accountId: $accountId, externalIds: $externalIds) {
        taskLists { externalId name linked isDefault todoListId }
        todoLists { ${TODO_LIST_FIELDS} }
        removedTodoListIds
      }
    }`,
    { accountId, externalIds },
    // The server runs the first import inline with an 8 s budget.
    { timeoutMs: 15_000 },
  );
  return data.setLinkedTaskLists;
};

export interface SyncIntegrationsPayload {
  accounts: IntegrationAccount[];
  /** true → the client re-fetches todoLists + walks todosPage and merges. */
  changed: boolean;
  /** true → nothing ran (1/min per account); not an error. */
  throttled: boolean;
}

/** Auth only (C10): a non-Pro user gets `paused` accounts and changed:false. Omit id = all accounts. */
export const syncIntegrations = async (accountId?: string): Promise<SyncIntegrationsPayload> => {
  const data = await graphqlRequest<{ syncIntegrations: SyncIntegrationsPayload }>(
    `mutation SyncIntegrations($accountId: ID) {
      syncIntegrations(accountId: $accountId) { accounts { ${ACCOUNT_FIELDS} } changed throttled }
    }`,
    { accountId: accountId ?? null },
    { timeoutMs: 15_000 },
  );
  return data.syncIntegrations;
};

/** F5: revive errored rows. Omit todoIds = every errored row of the account. */
export const retryIntegrationSync = async (accountId: string, todoIds?: string[]): Promise<BackendTodo[]> => {
  const data = await graphqlRequest<{ retryIntegrationSync: BackendTodo[] }>(
    `mutation RetryIntegrationSync($accountId: ID!, $todoIds: [ID!]) {
      retryIntegrationSync(accountId: $accountId, todoIds: $todoIds) { ${TODO_FIELDS} }
    }`,
    { accountId, todoIds: todoIds ?? null },
  );
  return data.retryIntegrationSync;
};

// ── [P3]: calendars (contract §7d) ──────────────────────────────────────────

export interface IntegrationCalendar {
  id: string;
  name: string;
  /** "#rrggbb" */
  color: string | null;
  primary: boolean;
  selected: boolean;
  /** The Rhemn prayer-times calendar: listed last, inert, never selectable. */
  isRhemn: boolean;
}

// Shared with the EventKit wrapper, which must not import this (network) module.
export type { CalendarEvent, CalendarFetchIssue, PrayerKey } from '../types';

export interface CalendarEventsPayload {
  events: CalendarEvent[];
  issues: CalendarFetchIssue[];
  fetchedAt: string;
}

/**
 * `extensions.grant` of a SCOPE_REQUIRED error (C15, C16, C19): which consent
 * the account is missing. null for any other error.
 */
export const scopeRequiredGrant = (err: unknown): IntegrationGrant | null => {
  if (!(err instanceof GraphqlResponseError) || err.code !== INTEGRATION_ERROR.scopeRequired) return null;
  const grant = err.extensions.grant;
  return grant === 'tasks' || grant === 'calendar_read' || grant === 'prayer_write' ? grant : null;
};

const CALENDAR_FIELDS = 'id name color primary selected isRhemn';

/** Auth + Pro. The first call persists [primary] as the selection (C17). */
export const getIntegrationCalendars = async (accountId: string): Promise<IntegrationCalendar[]> => {
  const data = await graphqlRequest<{ integrationCalendars: IntegrationCalendar[] }>(
    `query IntegrationCalendars($accountId: ID!) {
      integrationCalendars(accountId: $accountId) { ${CALENDAR_FIELDS} }
    }`,
    { accountId },
  );
  return data.integrationCalendars;
};

/** Replaces the selection (≤ 50); invalidates the server's event cache. */
export const setSelectedCalendars = async (accountId: string, calendarIds: string[]): Promise<IntegrationAccount> => {
  const data = await graphqlRequest<{ setSelectedCalendars: IntegrationAccount }>(
    `mutation SetSelectedCalendars($accountId: ID!, $calendarIds: [ID!]!) {
      setSelectedCalendars(accountId: $accountId, calendarIds: $calendarIds) { ${ACCOUNT_FIELDS} }
    }`,
    { accountId, calendarIds },
  );
  return data.setSelectedCalendars;
};

export const updateIntegrationSettings = async (
  accountId: string,
  settings: { meetingPrompts?: boolean },
): Promise<IntegrationAccount> => {
  const data = await graphqlRequest<{ updateIntegrationSettings: IntegrationAccount }>(
    `mutation UpdateIntegrationSettings($accountId: ID!, $meetingPrompts: Boolean) {
      updateIntegrationSettings(accountId: $accountId, meetingPrompts: $meetingPrompts) { ${ACCOUNT_FIELDS} }
    }`,
    { accountId, meetingPrompts: settings.meetingPrompts ?? null },
  );
  return data.updateIntegrationSettings;
};

/** Every Google account of the user, ≤ 72 h window. Partial failures come back as `issues`. */
export const getCalendarEvents = async (from: string, to: string): Promise<CalendarEventsPayload> => {
  const data = await graphqlRequest<{ calendarEvents: CalendarEventsPayload }>(
    `query CalendarEvents($from: String!, $to: String!) {
      calendarEvents(from: $from, to: $to) {
        events {
          id accountId calendarId provider title allDay start end color
          meetingUrl attendeeCount declined transparent status
        }
        issues { accountId code }
        fetchedAt
      }
    }`,
    { from, to },
  );
  return data.calendarEvents;
};

// ── [P5]: prayer times written to Google Calendar (contract §6, C19–C21) ───

export interface PrayerTimeInput {
  key: PrayerKey;
  /** Localized prayer name, ≤ 40 chars; blank → the English name (C21). */
  title?: string;
  /** ISO-8601 instant WITH offset: the adhan time. */
  start: string;
}

export interface PrayerDayInput {
  /** YYYY-MM-DD in the user's time zone. */
  date: string;
  prayers: PrayerTimeInput[];
}

export interface SyncPrayerTimesInput {
  accountId: string;
  /** IANA, e.g. "America/Chicago". */
  timeZone: string;
  /** "Prayer times for {city}." Never coordinates. */
  cityLabel?: string;
  days: PrayerDayInput[];
}

export interface PrayerTimesSyncPayload {
  account: IntegrationAccount;
  writtenThrough: string;
  eventCount: number;
  /** Identical payload within 24 h: nothing was sent to Google (C20). A success. */
  throttled: boolean;
}

/** Auth + Pro + the prayer_write grant (SCOPE_REQUIRED grant:"prayer_write" otherwise). */
export const syncPrayerTimesToCalendar = async (input: SyncPrayerTimesInput): Promise<PrayerTimesSyncPayload> => {
  const data = await graphqlRequest<{ syncPrayerTimesToCalendar: PrayerTimesSyncPayload }>(
    `mutation SyncPrayerTimesToCalendar($input: SyncPrayerTimesInput!) {
      syncPrayerTimesToCalendar(input: $input) {
        account { ${ACCOUNT_FIELDS} } writtenThrough eventCount throttled
      }
    }`,
    { input },
    { timeoutMs: 20_000 },
  );
  return data.syncPrayerTimesToCalendar;
};

/** Auth only: a lapsed-Pro user can always remove what Rhemn wrote. Keep → false, Remove → true. */
export const disablePrayerTimesInCalendar = async (
  accountId: string,
  removeExisting: boolean,
): Promise<IntegrationAccount> => {
  const data = await graphqlRequest<{ disablePrayerTimesInCalendar: IntegrationAccount }>(
    `mutation DisablePrayerTimesInCalendar($accountId: ID!, $removeExisting: Boolean!) {
      disablePrayerTimesInCalendar(accountId: $accountId, removeExisting: $removeExisting) { ${ACCOUNT_FIELDS} }
    }`,
    { accountId, removeExisting },
    { timeoutMs: 20_000 },
  );
  return data.disablePrayerTimesInCalendar;
};
